/**
 * Field-redaction allowlist applied to anything we forward to a sink
 * (logger output, analytics events, Sentry breadcrumbs).
 *
 * Mirrors `plans/03-architecture/security.md`: never let credentials,
 * cookies, tokens, or LinkedIn auth material leave the runtime in plaintext.
 *
 * The matcher is case-insensitive against object keys. Values are replaced
 * with `'[redacted]'`. Walks plain objects + arrays recursively. Primitives
 * pass through unchanged.
 */
const SECRET_KEY_PATTERNS = [
    /password/i,
    /token/i,
    /cookie/i,
    /authorization/i,
    /secret/i,
    /apikey/i,
    /api[_-]key/i,
    /li[_-]?at/i,
];
const REDACTED = '[redacted]';
/**
 * Query-string parameter names whose VALUE must be stripped from any URL we
 * forward (Sentry breadcrumb URLs, log lines, analytics event URLs). These
 * commonly carry bearer tokens or short-lived signed-link material.
 */
const SECRET_QUERY_PARAMS = ['token', 'access_token', 'apikey', 'api_key'];
/**
 * HTTP header names whose VALUE must be redacted before forwarding (Sentry's
 * `event.request.headers`, log fixtures, analytics payloads).
 */
const SECRET_HEADER_NAMES = [
    'authorization',
    'cookie',
    'set-cookie',
    'x-api-key',
    'x-auth-token',
];
/** Returns true if `key` should be redacted on outbound payloads. */
export function isSecretKey(key) {
    return SECRET_KEY_PATTERNS.some((pattern) => pattern.test(key));
}
function isPlainObject(value) {
    if (value === null || typeof value !== 'object')
        return false;
    const proto = Object.getPrototypeOf(value);
    return proto === null || proto === Object.prototype;
}
/**
 * Walk `input`, replacing the values of any key matching the secret
 * patterns with `'[redacted]'`. Returns a fresh object — does not mutate.
 *
 * Cycle-safe: a value already seen during traversal is short-circuited.
 */
export function redactFields(input, seen = new WeakSet()) {
    if (Array.isArray(input)) {
        return input.map((item) => redactValue(item, seen));
    }
    if (isPlainObject(input)) {
        if (seen.has(input))
            return input;
        seen.add(input);
        const out = {};
        for (const [key, value] of Object.entries(input)) {
            if (isSecretKey(key)) {
                out[key] = REDACTED;
            }
            else {
                out[key] = redactValue(value, seen);
            }
        }
        return out;
    }
    return input;
}
function redactValue(value, seen) {
    if (Array.isArray(value)) {
        return value.map((item) => redactValue(item, seen));
    }
    if (isPlainObject(value)) {
        return redactFields(value, seen);
    }
    return value;
}
/**
 * Strip the values of any secret query-string parameters from a URL, leaving
 * the rest of the URL intact. Returns the input unchanged if it doesn't
 * parse as a URL.
 */
export function redactUrl(input) {
    if (!input)
        return input;
    // Fast path — no `?` means no query string to strip.
    const qIdx = input.indexOf('?');
    if (qIdx === -1)
        return input;
    try {
        // URL needs an absolute base for paths-only inputs ("/foo?token=x"). We
        // pick a sentinel base so we can detect path-only inputs and emit
        // path-only output.
        const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(input);
        const base = isAbsolute ? undefined : 'https://__redact-base__/';
        const url = new URL(input, base);
        let mutated = false;
        for (const name of SECRET_QUERY_PARAMS) {
            if (url.searchParams.has(name)) {
                url.searchParams.set(name, REDACTED);
                mutated = true;
            }
        }
        if (!mutated)
            return input;
        if (isAbsolute)
            return url.toString();
        // Path-only — strip the sentinel host back out.
        return `${url.pathname}${url.search}${url.hash}`;
    }
    catch {
        return input;
    }
}
/**
 * Walk a Headers-like object (Headers instance, plain record) and return a
 * new record where any secret header's value is replaced with `[redacted]`.
 * Header names are normalised to lower-case in the output.
 */
export function redactHeaders(headers) {
    const out = {};
    if (!headers)
        return out;
    const entries = [];
    if (headers instanceof Headers) {
        headers.forEach((value, key) => entries.push([key, value]));
    }
    else {
        for (const [k, v] of Object.entries(headers)) {
            if (typeof v === 'string')
                entries.push([k, v]);
        }
    }
    for (const [rawKey, value] of entries) {
        const key = rawKey.toLowerCase();
        out[key] = SECRET_HEADER_NAMES.includes(key) ? REDACTED : value;
    }
    return out;
}
/** List of HTTP headers that must always be redacted on outbound payloads. */
export const REDACTED_HEADER_NAMES = SECRET_HEADER_NAMES;
/** List of query parameters that must be stripped from URLs we forward. */
export const REDACTED_QUERY_PARAMS = SECRET_QUERY_PARAMS;
//# sourceMappingURL=redact.js.map